/**
 * Where the Charter Board's claim details come from (M5 plan C17). The galaxy view carries every system's claim
 * summary, but the per-faction scores, trends and flip streaks reach the main thread only in the current system's
 * `SystemEconomyView.claim`. For the rest the Board decodes the v2 save's claim columns (as the Reach monitor does,
 * plan C13): `score`, `trend` (ring), `ops`/`sec`/`inf` (EMAs, shown as shares of 100 within the system),
 * `holder`, `challenger` and `streak`. Outlaws never score (factions.json `outlaw`). The founded player corporation
 * scores as `'player'` and its held systems carry the corporation block (name, security band from the `sec` EMA,
 * tariff set from its `tariff` column; D-424 D5, spec §27.7); `decodeBoardCorp` reads its name and livery colour.
 *
 * UI events only (on the Board's open and its refresh).
 */
import factionsJson from '../../data/strategic/factions.json'
import claimsJson from '../../data/strategic/claims.json'
import corpJson from '../../data/strategic/corp.json'
import { decodeColumn, fromBase64 } from '../../strategic/codec'
import { PLAYER_FACTION_ID, type CharterVote, type ClaimView, type CorpFactionId, type FleetSave, type SecurityBand, type StrategicSave } from '../../strategic/types'
import type { BoardCorp, ClaimDetail } from './boardModel'

const OUTLAW: ReadonlySet<string> = new Set(factionsJson.factions.filter((f) => f.outlaw).map((f) => f.id))
const FLIP_DAYS: number = claimsJson.holder.flipDays
const BANDS: { medium: number; high: number } = corpJson.claims.securityBands
/** As `claims/securityBand.ts`: the EMA is compared at one decimal. */
const BAND_PRECISION = 10

/** The save's founded corporation record, or null (an old save or before founding). */
function foundedCorp(save: StrategicSave): FleetSave['corp'] | null {
  if (!('fleet' in save)) return null
  const c = save.fleet.corp
  return c.founded && c.faction >= 0 ? c : null
}

/** The corporation's band for a claim Security EMA (corp.json `claims.securityBands`). */
export function bandOfSecurity(security: number): SecurityBand {
  const v = Math.round(security * BAND_PRECISION) / BAND_PRECISION
  if (v >= BANDS.high) return 'high'
  if (v >= BANDS.medium) return 'medium'
  return 'low'
}

/** The founded corporation's name and livery colour from the save (null before founding). */
export function decodeBoardCorp(save: StrategicSave): BoardCorp | null {
  const c = foundedCorp(save)
  return c === null ? null : { name: c.name, color: c.livery.primary }
}

function column(cols: Record<string, string>, name: string): Float64Array {
  const s = cols[name]
  return s === undefined ? new Float64Array(0) : decodeColumn(fromBase64(s))
}

/** Claim details by system id, decoded from a v2 save. `systemIds` is the galaxy's system order (index → id). */
export function decodeClaimDetails(save: StrategicSave, systemIds: readonly string[]): Map<string, ClaimDetail> {
  const out = new Map<string, ClaimDetail>()
  const cl = save.charter.claims
  const score = column(cl.cols, 'score')
  const S = systemIds.length
  if (S === 0 || score.length === 0) return out
  const fc = Math.floor(score.length / S)
  const trend = column(cl.cols, 'trend')
  const ops = column(cl.cols, 'ops')
  const sec = column(cl.cols, 'sec')
  const inf = column(cl.cols, 'inf')
  const holder = column(cl.cols, 'holder')
  const challenger = column(cl.cols, 'challenger')
  const streak = column(cl.cols, 'streak')
  const days = fc > 0 ? Math.floor(trend.length / (S * fc)) : 0
  const filled = Math.min(cl.trendFilled, days)
  const corp = foundedCorp(save)
  const corpF = corp?.faction ?? -1
  const corpTariff = corp !== null ? column(corp.cols, 'tariff') : new Float64Array(0)
  const ids: (CorpFactionId | null)[] = []
  for (let f = 0; f < fc; f++) {
    const id = f === corpF ? PLAYER_FACTION_ID : (save.factions[f]?.id ?? null)
    ids.push(id !== null && !OUTLAW.has(id) ? id : null)
  }
  const share = (col: Float64Array, base: number, f: number): number => {
    let sum = 0
    for (let g = 0; g < fc; g++) if (ids[g] !== null) sum += col[base + g] ?? 0
    return sum > 0 ? (100 * (col[base + f] ?? 0)) / sum : 0
  }
  for (let sys = 0; sys < S; sys++) {
    const base = sys * fc
    const h = holder[sys] ?? -1
    const scores: ClaimView['scores'] = []
    for (let f = 0; f < fc; f++) {
      const id = ids[f]
      if (id === null) continue
      const s = score[base + f] ?? 0
      if (!(s > 0) && f !== h) continue
      const t: number[] = []
      for (let back = filled - 1; back >= 0; back--) {
        const slot = (((cl.trendHead - 1 - back) % days) + days) % days
        t.push(trend[(base + f) * days + slot] ?? 0)
      }
      scores.push({
        faction: id,
        ops: share(ops, base, f),
        sec: share(sec, base, f),
        inf: share(inf, base, f),
        s,
        trend: t,
      })
    }
    scores.sort((a, b) => b.s - a.s)
    const c = challenger[sys] ?? -1
    const cid = c >= 0 ? (ids[c] ?? null) : null
    const held = corp !== null && h === corpF
    out.set(systemIds[sys], {
      ...(held
        ? { corp: { name: corp.name, band: bandOfSecurity(sec[base + corpF] ?? 0), tariffSet: (corpTariff[sys] ?? -1) >= 0 } }
        : {}),
      scores,
      flip:
        cid !== null
          ? {
              challenger: cid,
              daysLeft: Math.max(0, FLIP_DAYS - (streak[sys] ?? 0)),
            }
          : null,
    })
  }
  return out
}

/** The save's Charter votes. */
export function decodeVotes(save: StrategicSave): CharterVote[] {
  return save.charter.votes.list
}

/** The save's faction day (`dayS`: the faction day length in strategic seconds). */
export function saveDay(save: StrategicSave, dayS: number): number {
  return Math.floor(save.time / dayS)
}
